import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import useWindowDimensions from "../../helpers/WindowWidth";
import { logout } from "../../actions/auth";
import { hardLogoutOrenda } from "./orendaUserManager";
import { clearOrendaAuth } from "./orendaAuthStorage";

// Shared layout for every screen in the Orenda onboarding flow.
// Same visual system as OrendaDashboard: ink left panel floating on a
// continuous pink canvas, classic header hidden, no white gutters.
//
//   ┌───────────────┬──────────────────────────────┐
//   │  ink panel    │   {main content on pink}     │
//   │  bopayments   │                              │
//   │  greeting     │                              │
//   │  stepper      │                              │
//   │               │                              │
//   │  sign out     │                              │
//   └───────────────┴──────────────────────────────┘
//
// Props
//   currentStep    the Orenda application status.currentStep value
//   pagename       h1 for the left panel (string or JSX)
//   pagemsg        subtitle under the h1
//   children       the actual step screen
const OrendaShell = ({ currentStep, pagename, pagemsg, children }) => {
  const { width } = useWindowDimensions();
  const dispatch = useDispatch();

  // Paint the entire page pink while onboarding is mounted — same mechanism
  // as the dashboard. See the body.orenda-dash-body rules in screen1.css.
  useEffect(() => {
    document.body.classList.add("orenda-dash-body");
    return () => document.body.classList.remove("orenda-dash-body");
  }, []);

  const signOutEverything = () => {
    try { clearOrendaAuth(); } catch { /* ignore */ }
    dispatch(logout());
    hardLogoutOrenda();
  };

  return (
    <div className="row minvh-100 orenda-dash orenda-shell-v2">
      {width > 767 && (
        <aside className="col-lg-3 col-md-4 orenda-nav orenda-shell-nav">
          <div className="bo-sidebar-outer">
            <div className="orenda-nav-logo">
              <div className="orenda-nav-wordmark">
                <span className="orenda-nav-wordmark-bo">bo</span>
                <span className="orenda-nav-wordmark-payments">payments</span>
              </div>
            </div>

            <div className="orenda-shell-hero">
              <h1 className="orenda-shell-hero-title">{pagename}</h1>
              {pagemsg && (
                <p className="orenda-shell-hero-sub">{pagemsg}</p>
              )}
            </div>

            <OrendaStepperVertical currentStep={currentStep} />

            <div className="orenda-nav-footer">
              <button
                type="button"
                className="orenda-nav-logout"
                onClick={signOutEverything}
              >
                <LogoutIcon />
                <div>
                  <div className="orenda-nav-logout-label">Sign out</div>
                </div>
              </button>
            </div>
          </div>
        </aside>
      )}

      <main className="col-md-8 col-lg-9 orenda-dash-canvas orenda-shell-main">
        <div className="orenda-shell-content-wrap">
          {width <= 767 && (
            <div className="orenda-shell-mobile-head">
              <div className="orenda-nav-wordmark" style={{ color: "#1a1920" }}>
                <span style={{ color: "#1a1920" }}>bo</span>
                <span style={{ color: "#89455e", fontWeight: 400 }}>payments</span>
              </div>
              <button
                type="button"
                className="orenda-mobile-signout-link"
                onClick={signOutEverything}
              >
                Sign out
              </button>
            </div>
          )}
          <div className="orenda-shell-content">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

// Onboarding funnel. Vertical stepper on the ink sidebar.
const STEPS = [
  { id: "legal", label: "Legal" },
  { id: "info", label: "Your details" },
  { id: "identity", label: "Identity" },
  { id: "review", label: "Review" },
  { id: "done", label: "Done" },
];

const currentStepToMilestone = (currentStep) => {
  switch (currentStep) {
    case "LEGAL_AGREEMENTS":
      return "legal";
    case "KYC_FORM":
    case "KYC_INTERNAL":
    case "KYC_INTERNAL_VALIDATION_ERRORS":
    case "KYC":
    case "ADD_UBOS":
      return "info";
    case "IDENTITY_VERIFICATION":
    case "KYC_SUMSUB":
      return "identity";
    case "PROCESSING":
    case "IN_REVIEW":
    case "RISK":
    case "PROVIDER_DOCUMENTS":
      return "review";
    case "COMPLETED":
    case "DashBoard":
    case "Dashboard":
    case "DASHBOARD":
    case "HOLDER_CREATION":
    case "ACCOUNT_CREATION":
    case null:
    case undefined:
      return "done";
    default:
      return "info";
  }
};

const OrendaStepperVertical = ({ currentStep }) => {
  const activeId = currentStepToMilestone(currentStep);
  const activeIndex = STEPS.findIndex((s) => s.id === activeId);

  return (
    <ol className="orenda-vstepper" role="list">
      {STEPS.map((step, i) => {
        const state =
          i < activeIndex ? "done" : i === activeIndex ? "active" : "pending";
        return (
          <li
            key={step.id}
            className={`orenda-vstepper-step orenda-vstepper-step--${state}`}
          >
            <div className="orenda-vstepper-marker">
              {state === "done" ? "✓" : i + 1}
            </div>
            <div className="orenda-vstepper-label">{step.label}</div>
            {i < STEPS.length - 1 && (
              <div className="orenda-vstepper-rail" aria-hidden />
            )}
          </li>
        );
      })}
    </ol>
  );
};

const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

export default OrendaShell;
